import { ApiError as ApiErrorSchema } from '@cloudsign/shared';
import type { ApiError, ErrorCode } from '@cloudsign/shared';
import type { z } from 'zod';

const API_BASE = '/api/v1';

/** Thrown for every non-2xx answer. `messageKey` is an i18n key. */
export class ApiClientError extends Error {
  readonly status: number;
  readonly code: ErrorCode;
  readonly messageKey: string;
  readonly params: Record<string, string | number> | undefined;
  readonly fields: Record<string, string> | undefined;

  constructor(status: number, body: ApiError['error']) {
    super(body.messageKey);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = body.code;
    this.messageKey = body.messageKey;
    this.params = body.params;
    this.fields = body.fields;
  }

  get body(): ApiError['error'] {
    return { code: this.code, messageKey: this.messageKey, params: this.params, fields: this.fields };
  }
}

export type QueryValue = string | number | boolean | null | undefined;
export type Query = Record<string, QueryValue>;

type Listener = (error: ApiClientError) => void;
let authListener: Listener | null = null;

/** The app registers one handler for 401s (session expired or login not finished). */
export function setAuthFailureListener(fn: Listener | null): void {
  authListener = fn;
}

export function buildQuery(query?: Query): string {
  if (!query) return '';
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

interface RequestOptions<S extends z.ZodType | undefined> {
  query?: Query;
  body?: unknown;
  form?: FormData;
  /** Validate and type the response. Validation runs in development and tests only. */
  schema?: S;
  /** Absolute path (not under /api/v1), e.g. /api/health. */
  raw?: boolean;
  /** Do not trigger the sign-in redirect on 401 (used by the capabilities probe). */
  quiet401?: boolean;
}

async function request<S extends z.ZodType | undefined>(
  method: string,
  path: string,
  opts: RequestOptions<S> = {},
): Promise<S extends z.ZodType ? z.infer<S> : unknown> {
  const url = `${opts.raw ? '' : API_BASE}${path}${buildQuery(opts.query)}`;
  const headers: Record<string, string> = { Accept: 'application/json' };
  let body: BodyInit | undefined;
  if (opts.form) {
    body = opts.form;
  } else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  let res: Response;
  try {
    res = await fetch(url, { method, headers, body, credentials: 'same-origin' });
  } catch {
    throw new ApiClientError(0, { code: 'internal', messageKey: 'error.network' });
  }
  if (res.status === 204) {
    return undefined as never;
  }
  const text = await res.text();
  let json: unknown = undefined;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = undefined;
    }
  }
  if (!res.ok) {
    const parsed = ApiErrorSchema.safeParse(json);
    const err = new ApiClientError(
      res.status,
      parsed.success ? parsed.data.error : { code: 'internal', messageKey: 'error.internal' },
    );
    // Only a lost session sends people back to sign in; a wrong code or password is shown where it happened.
    if (res.status === 401 && !opts.quiet401 && (err.code === 'mfa_required' || err.messageKey === 'error.unauthenticated')) authListener?.(err);
    throw err;
  }
  // 202 (accepted) may carry a body (a job or export record) or none (for example forgot-password): return what there is.
  if (res.status === 202 && json === undefined) return undefined as never;
  if (opts.schema && import.meta.env.DEV) {
    return opts.schema.parse(json) as never;
  }
  return json as never;
}

/** Fetches a file (CSV, Excel, PDF) as a Blob, with the file name from Content-Disposition when given. */
async function download(path: string, opts: { query?: Query } = {}): Promise<{ blob: Blob; filename: string | null }> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}${buildQuery(opts.query)}`, { method: 'GET', credentials: 'same-origin' });
  } catch {
    throw new ApiClientError(0, { code: 'internal', messageKey: 'error.network' });
  }
  if (!res.ok) {
    let json: unknown = undefined;
    try {
      json = await res.json();
    } catch {
      json = undefined;
    }
    const parsed = ApiErrorSchema.safeParse(json);
    const err = new ApiClientError(
      res.status,
      parsed.success ? parsed.data.error : { code: 'internal', messageKey: 'error.internal' },
    );
    if (res.status === 401 && (err.code === 'mfa_required' || err.messageKey === 'error.unauthenticated')) authListener?.(err);
    throw err;
  }
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  return { blob: await res.blob(), filename: match?.[1] ?? null };
}

export const api = {
  download,
  get: <S extends z.ZodType | undefined = undefined>(path: string, opts?: RequestOptions<S>) =>
    request('GET', path, opts),
  post: <S extends z.ZodType | undefined = undefined>(path: string, opts?: RequestOptions<S>) =>
    request('POST', path, opts),
  patch: <S extends z.ZodType | undefined = undefined>(path: string, opts?: RequestOptions<S>) =>
    request('PATCH', path, opts),
  put: <S extends z.ZodType | undefined = undefined>(path: string, opts?: RequestOptions<S>) =>
    request('PUT', path, opts),
  delete: <S extends z.ZodType | undefined = undefined>(path: string, opts?: RequestOptions<S>) =>
    request('DELETE', path, opts),
};
